export function isMarketClosed(market, now = new Date()) {
  if (!market) return false;
  if (Number(market?.is_active ?? 1) === 0) return true;
  if (String(market?.status || "").toLowerCase() === "closed") return true;

  const schedule = getMarketWindowForNow(market, now);
  if (schedule.closed) return true;

  const currentMinutes = getIndiaCurrentMinutes(now);
  if (String(market?.market_type || "normal").toLowerCase() !== "starline" && currentMinutes < 60) return true;

  const closeMinutes = parseTimeToMinutes(schedule.close_time);
  if (closeMinutes == null) return false;
  const openMinutes = parseTimeToMinutes(schedule.open_time);
  const closeCutoff = openMinutes != null && closeMinutes <= openMinutes ? closeMinutes + 1440 : closeMinutes;
  return currentMinutes >= closeCutoff;
}

export function getMarketStatusLabel(market, now = new Date()) {
  return isMarketClosed(market, now) ? "Closed Now" : "Open Now";
}

export function getSuggestedSession(market, now = new Date()) {
  if (!market) return "OPEN";

  const currentMinutes = getIndiaCurrentMinutes(now);
  if (currentMinutes >= 60 && currentMinutes < 120) {
    return "OPEN";
  }

  const openMinutes = parseTimeToMinutes(getMarketWindowForNow(market, now).open_time);
  if (openMinutes == null) return "OPEN";
  return currentMinutes >= openMinutes ? "CLOSE" : "OPEN";
}

export function getMarketWindowForNow(market, now = new Date()) {
  const baseOpen = market.open_time || market.open || market.start_time || "";
  const baseClose = market.bid_close_time || market.close_time || market.close || market.end_time || "";
  const day = getIndiaWeekdayShort(now);
  const schedule = normalizeSchedule(market.schedule_json, baseOpen, baseClose);
  const current = schedule.find((item) => item.day === day) || {
    day,
    open_time: baseOpen,
    close_time: baseClose,
    closed: false,
  };

  return {
    day,
    open_time: current.open_time || baseOpen,
    close_time: current.close_time || baseClose,
    closed: Boolean(current.closed) || isClosedOnDay(market, now),
  };
}

function isClosedOnDay(market, now) {
  const rawDays = String(market.closed_days || "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  if (!rawDays.length) return false;

  const today = getIndiaWeekdayShort(now).toLowerCase();
  return rawDays.some((value) => today.startsWith(value.slice(0, 3)));
}

function normalizeSchedule(value, fallbackOpen, fallbackClose) {
  const parsed = parseSchedule(value);
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => {
    const found = parsed.find((item) => String(item?.day || "").slice(0, 3).toLowerCase() === day.toLowerCase());
    const open_time = String(found?.open_time || found?.open || fallbackOpen || "").trim();
    const close_time = String(found?.close_time || found?.close || fallbackClose || "").trim();
    return {
      day,
      open_time,
      close_time,
      closed: Boolean(found?.closed || found?.is_closed || found?.enabled === false) || (!open_time && !close_time),
    };
  });
}

function parseSchedule(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    if (Array.isArray(value.days)) return value.days;
    return Object.entries(value).map(([day, entry]) => ({ day, ...entry }));
  }

  try {
    const parsed = JSON.parse(String(value));
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.days)) return parsed.days;
    if (parsed && typeof parsed === "object") {
      return Object.entries(parsed).map(([day, entry]) => ({ day, ...entry }));
    }
  } catch {
    return [];
  }

  return [];
}

function parseTimeToMinutes(value) {
  if (!value) return null;
  const raw = String(value).trim().toLowerCase();

  const meridiemMatch = raw.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (meridiemMatch) {
    let hour = Number(meridiemMatch[1]);
    const minute = Number(meridiemMatch[2]);
    const meridiem = meridiemMatch[3];
    if (meridiem === "pm" && hour !== 12) hour += 12;
    if (meridiem === "am" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }

  const timeMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (timeMatch) {
    const hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2]);
    return hour * 60 + minute;
  }

  return null;
}

function getIndiaCurrentMinutes(now) {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(now);
  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);
  return hour * 60 + minute;
}

function getIndiaWeekdayShort(now) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata",
      weekday: "short",
    }).format(now);
  } catch {
    return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][now.getDay()] || "Sun";
  }
}
